import { useState, type FC } from "react";
import { CheckCircle2, Link2Off, Mail } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { PublicOutgoingIntakesAPI } from "@/api/publicOutgoingIntakesApi";
import { APIError } from "@/api/utils/errors";

export type LinkGoneCode = "link_replaced" | "link_revoked" | "link_expired";

export type LinkGone = {
  code: LinkGoneCode;
  outgoingIntakeId: string;
  firmName: string | null;
  maskedEmail: string | null;
};

const isLinkGoneCode = (code: string): code is LinkGoneCode =>
  code === "link_replaced" ||
  code === "link_revoked" ||
  code === "link_expired";

/** The 410 a dead public link answers with, or null for any other failure. */
export const linkGoneFrom = (error: unknown): LinkGone | null => {
  if (!(error instanceof APIError) || error.status !== 410) return null;
  const { details }: { details?: unknown } = error;
  if (!details || typeof details !== "object") return null;
  const read = (key: string): string | null => {
    const value: unknown = Reflect.get(details, key);
    return typeof value === "string" && value.trim() ? value.trim() : null;
  };
  const code = read("code");
  const outgoingIntakeId = read("outgoing_intake_id");
  if (!code || !isLinkGoneCode(code) || !outgoingIntakeId) return null;
  return {
    code,
    outgoingIntakeId,
    firmName: read("law_firm_name"),
    maskedEmail: read("masked_email")
  };
};

type Props = {
  link: LinkGone;
};

type SendState = "idle" | "sending" | "sent" | "rate_limited" | "failed";

export const LinkGoneState: FC<Props> = ({ link }) => {
  const [state, setState] = useState<SendState>("idle");
  const firm = link.firmName || "your law firm";
  const replaced = link.code === "link_replaced";

  const title = replaced
    ? "This link was replaced"
    : link.code === "link_expired"
    ? "This link has expired"
    : "This link no longer works";
  const body = replaced
    ? `${firm} sent you a newer link, so this one stopped working. Anything you already answered is saved.`
    : link.code === "link_expired"
    ? `Links stay open for a limited time. Ask for a new one and ${firm} will be told you need it.`
    : `${firm} turned this link off. If you still need to send your answers, ask for a new one.`;
  const sentText = replaced
    ? `Check ${link.maskedEmail ?? "your inbox"} for an email from ${firm}.`
    : `We let ${firm} know you need a new link.`;

  const send = async () => {
    setState("sending");
    try {
      await PublicOutgoingIntakesAPI.resendLink(link.outgoingIntakeId);
      setState("sent");
    } catch (error) {
      setState(
        error instanceof APIError && error.status === 429
          ? "rate_limited"
          : "failed"
      );
    }
  };

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-background px-6 py-12 text-foreground">
      <div className="flex w-full max-w-md flex-col items-start gap-4">
        <div className="flex size-10 items-center justify-center rounded-full bg-muted">
          <Link2Off className="size-5 text-muted-foreground" aria-hidden />
        </div>
        <div className="flex flex-col gap-1.5">
          <h1 className="text-xl font-semibold">{title}</h1>
          <p className="text-sm text-muted-foreground">{body}</p>
        </div>

        {state === "sent" ? (
          <p
            role="status"
            className="flex items-center gap-2 text-sm text-foreground"
          >
            <CheckCircle2 className="size-4 text-primary" aria-hidden />
            {sentText}
          </p>
        ) : (
          <Button
            variant="primary"
            onClick={send}
            disabled={state === "sending"}
          >
            {state === "sending" ? <Spinner /> : <Mail aria-hidden />}
            {replaced ? "Email me the latest link" : "Ask for a new link"}
          </Button>
        )}

        {state === "rate_limited" ? (
          <p role="alert" className="text-sm text-destructive">
            A link was requested a moment ago. Try again in a few minutes.
          </p>
        ) : state === "failed" ? (
          <p role="alert" className="text-sm text-destructive">
            That didn&apos;t go through. Try again, or contact {firm}.
          </p>
        ) : null}

        {replaced ? (
          <p className="text-xs text-muted-foreground">
            We only send it to the address {firm} has on file
            {link.maskedEmail ? ` (${link.maskedEmail})` : ""}.
          </p>
        ) : null}
      </div>
    </div>
  );
};
